import { Injectable, Component, Inject, EventEmitter } from '@angular/core';
import { Location } from '@angular/common';
import {Router} from '@angular/router';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material';
import {Title} from '@angular/platform-browser';

import {from} from 'rxjs/observable/from';
import {of} from 'rxjs/observable/of';
import {catchError, concatMap, mergeMap, takeWhile, finalize} from 'rxjs/operators';

import {ApiService} from '../shared/api.service';
import {Assignment} from '../assignments/assignment.model';
import {AssignmentsService} from '../shared/assignments.service';
import {ImagesService, ImagesErrorhDialog} from '../shared/images.service';
import {Slide} from './slide/slide.model';
import {NotificationService} from '../shared/notification.service';
import {UserService} from '../shared/user.service';
import { Site } from '../shared/site.model';
    
@Injectable()
export class SlideshowService {
    assignment;
    autosave;
    backup;
    slideIndex = 0;
    slides = [];
    title = '';
    tags = [];
    locked = true;
    publishError = '';
    quiz = false;
    tiers = [];
    slidesLoaded = new EventEmitter();
    saving = false;
    publishing = false;
    lockCheckOpen = false;
    _locked = new EventEmitter();
    autosaving = false;
    _autosaveChecked = false;
    autosaveChecked = new EventEmitter();
    autosaveOpen = false;
    lastModified;
    heartbeatSubscription;
    showSlideNav = false;
    slidesComplete = new EventEmitter();
    slidesCompleteSubscription;
    assignmentComplete = new EventEmitter();
    assignmentCompleteSubscription;
    sending = false;
    warning = false;
    whosEditingSubscription;
    featuredImageChange = new EventEmitter();
    resultsChanged = new EventEmitter();
    reload = false;
    clones = [];
    answerSummaryPlaceholder = 'Correct Answer Summary';
    messages = [];
    commentAdded = new EventEmitter();
    assignmentLoaded = new EventEmitter();
    _assignmentLoaded = false;
    targetPublishDateChange = new EventEmitter();
    statusChange = new EventEmitter();

    constructor(
        private api: ApiService,
        private assignmentService: AssignmentsService,
        public dialog: MatDialog,
        private images: ImagesService,
        private location: Location,
        private notifyService: NotificationService,
        private router: Router,
        private titleService: Title,
        private userService: UserService
    ) {
        this.slidesLoaded.subscribe(slideIndex => {
            this.backupLocally();
            this.whosEditing();
            this.setSEOData();
            this.getImages();
        });
    }
    getClones() {
        this.api.doAction('post', 'clone', {
            action: 'get',
            data: this.assignment.clones
        }).subscribe(
            data => {
                this.clones = data;
            }
        );
    }
    getImages() {
        this.images.loadImages(this.assignment, this.slides);
    }
    isFeaturedImage(img) {
        const images = this.slides[0].images;
        if( images.length == 0 ) {
            return false;
        }
        return images[0].id == img.id;
    }
    toggleFeaturedImage(img) {
        if( this.locked ) {
            return;
        }
        let images = this.slides[0].images;
        if( images.length == 0 || images[0].id != img.id ) {
            this.assignment.featured_media = img.id;
            this.slides[0].images = [img];
            this.featuredImageChange.emit();
            return;
        }
        if( images[0].id == img.id ) {
            this.assignment.featured_media = 0;
            this.slides[0].images = [];
            this.featuredImageChange.emit();
        }
    }
    getSlideNumber(slide, i) {
        let num = i;
        if( slide.intro ) {
            num = 'in';
        }
        if( this.quiz && slide.outro ) {
            num = 'r';
        }
        return num;
    }
    setSEOData() {
        if( !this.assignment ) {
            return;
        }
        if( this.assignment.metaDescription == '' ) {
            this.assignment.metaDescription = this.slides[0].metaDescription;
        }
        if( this.assignment.metaTitle == '' ) {
            this.assignment.metaTitle = this.assignment.articleTitle;
        }
    }
    whosEditing() {
        this.whosEditingSubscription = this.api.heartbeat
            .pipe(
                takeWhile(val => this.assignment !== null),
                finalize(() => {
                    this.whosEditingSubscription.unsubscribe();
                })
            ).subscribe(
                data => {
                    if( this.locked ) {
                        this.api.doAction('post', 'meta', {
                            action: 'get',
                            id: this.assignment.id,
                            fields: [
                                {
                                    name: 'lock'
                                }
                            ]
                        }).pipe(takeWhile(val => this.assignment !== null)).subscribe(
                            data => {
                                const lock = data.shift() || {value: null};
                                this.assignment.lock = lock.value;
                            }
                        );
                    }
                }
            );
    }
    cloneMe(me) {
        return JSON.parse(JSON.stringify(me));
    }
    getBackup({slides, assignment}) {
        let a = this.cloneMe(assignment);
        delete a.lock;
        let s = this.cloneMe(slides);
        return {
            assignment: a,
            slides: s
        };
    }
    backupLocally() {
        if( this.locked || this.api.getProp('loggedOut') || !this.assignment ) {
            return;
        }
        this.autosave = this.getBackup(this);
        this.backup = this.getBackup(this);
    }
    objEq(o, n) {
        return JSON.stringify([o]) === JSON.stringify([n]);
    }
    maybeAutosave() {
        if( this.locked || this.saving || this.api.getProp('loggedOut') || !this.assignment || this.autosaving ) {
            return;
        }
        let user = this.api.getProp('currentUserId');
        let current = this.getBackup(this);
        let autosave = this.autosave;
        if( !this.objEq(autosave, current) ) {
            this.autosaving = true;
            let posts = [current.assignment].concat(current.slides)
            this.api.doAction('post', 'autosave', {
                    id: current.assignment.id,
                    posts: posts,
                    user: user,
                    published: current.assignment.published, // TODO: why do we need this?
                    action: 'update'
                }).pipe(
                    finalize(() => {
                        this.autosaving = false;
                    }
                )).subscribe(
                    data => {
                        this.autosaving = false;
                        this.autosave = current;
                    }
                );
        }
    }
    titleChange(slide) {
        this.warning = false;
        let title = slide.title || '';
        if( slide.intro ) {
            title = this.assignment.articleTitle;
        }
        let cleanTitle = this.cleanContent(title);
        slide.metaTitle = cleanTitle.substring(0, 70);
    }
    cleanContent(content) {
        content = content.replace(/<([abipsu]|sup|sub)[^>]*>|<\/([abipsu]|sup|sub)>/g, '');
        return content;
    }
    contentChange(html, slide) {
        let content = [];
        let desc = '';
        let div = document.createElement('div');
        div.innerHTML = html;
        let ptags = div.querySelectorAll('p');
        for(var i = 0; i < ptags.length; i++) {
            let p = ptags[i];
            let inner = p.innerHTML;
            desc += p.innerText + ' ';
            if( inner.trim() !== '' ) {
                content.push({
                    text: inner
                });
            }            
        }
        slide.content = content;
        slide.metaDescription = desc.substr(0, 160);
    }
    calculateTiers() {
        let tiers = [];
        let slideCount = this.slides.length;
        if( slideCount < 3 ) {
            return;
        }
        let questionCount = slideCount - 2;
        let resultCount = this.slides[slideCount - 1].content.length;
        let prev = 0;
        for(var i = 0; i < resultCount; i++) {
            let resultNum = (i + 1) / resultCount;
            let total = Math.floor(questionCount * resultNum);
            if( i == (resultCount - 1)) {
                total = questionCount;
            }
            tiers.push({
                min: prev == 0 ? 0 : prev + 1,
                max: total
            });
            prev = total;
        }
        this.tiers = tiers;
    }
    resetSlideshow() {
        if( !this.locked ) {
            this.clearLock();
        }        
        this.assignment = null;
        this.autosave = null;
        this.slideIndex = 0;
        this.slides = [];
        this.tags = [];
        this.publishError = '';
        this.quiz = false;
        this.tiers = [];
        this.saving = false;
        this.publishing = false;
        this.lockCheckOpen = false;
        this.autosaving = false;
        this._autosaveChecked = false;
        this.lastModified = '';
        this.showSlideNav = false;
        this.images.reset();
    }
    stop() {
        if( this.heartbeatSubscription ) {
            this.heartbeatSubscription.unsubscribe();
        }
        if( this.whosEditingSubscription ) {
            this.whosEditingSubscription.unsubscribe();
        }
    }
    loadSlideshow(id) {
        if( this.reload ) {
            this.reload = false;
            return;
        }
        this.resetSlideshow();
        let assignment = this.assignmentService.getAssignment(id);
        if( assignment ) {
            this.setSlideshow(assignment);
        } else {
            this.api.doAction('post', 'assignments', {
                action: 'get',
                id: id
            }).subscribe(assignment => {
                assignment = new Assignment(assignment);
                (() => this.setSlideshow(assignment))();
            });
        }
        this.notifyService.filter([id]); 
    }
    setLock() {
        const subscription = this.assignmentService.assignmentLocked.subscribe(locked => {
            if( locked ) {
                this.locked = false;
                this._locked.emit(this.locked);
            }
            subscription.unsubscribe();
        });
        this.assignmentService.lock(this.assignment);
    }
    lockCheck() {
        if( this.api.getProp('loggedOut') || this.lockCheckOpen || this.locked || !this.assignment || this.saving || this.publishing )  return;
        
        this.api.doAction('post', 'meta', {
                action: 'get',
                id: this.assignment.id,
                fields: [
                    {
                        name: 'lock'
                    }
                ]
            })
            .pipe(
                takeWhile(val => this.assignment !== null)
            ).subscribe(
                data => {
                    const currentLock = data.shift() || {value: false};
                    let userId = this.api.getProp('currentUserId');
                    if( currentLock.value && currentLock.value.user != userId ) {
                        this.lockCheckOpen = true;
                        let dialog = this.dialog.open(TakeoverDialog, {
                            disableClose: true,
                            data: {
                                user: currentLock.value.name
                            }
                        });
                        dialog.afterClosed().subscribe(cancel => {
                            this.lockCheckOpen = false;
                            if( cancel ) {
                                this.locked = true;
                            } else {
                                this.locked = false;
                                this.assignmentService.lock(this.assignment);
                            }
                            this._locked.emit(this.locked);
                        });
                    } else if( currentLock.value && currentLock.value.user == userId && (currentLock.value.time + (1000 * 60 * 5)) < (new Date).getTime() ) {
                        this.assignmentService.lock(this.assignment);
                    }
                }
            );
    }
    deleteAutosave() {
        this.api.doAction('post', 'autosave', {
                id: this.assignment.id,
                user: this.api.getProp('currentUserId'),
                action: 'delete'
            })
            .pipe(
                takeWhile(val => this.assignment !== null)
            ).subscribe(
                data => {
                    console.log('Autosave deleted.', data);
                }
            );
    }
    getAutosave(assignment) {
        let user = this.api.getProp('currentUserId');
        let clones = assignment.clones;
        this.api.doAction('post', 'autosave', {
            id: assignment.id,
            user: user,
            date: this.lastModified,
            action: 'get'
        }).pipe(
            takeWhile(val => this.assignment !== null),
            finalize(() => {
                this.autosaveChecked.emit(true);
            })
        ).subscribe(
            data => {
                if( data.found && Array.isArray(data.posts) ) {
                    let posts = data.posts;
                    let current = this.getBackup(this);
                    let stored = this.getBackup({
                        assignment: posts.shift(),
                        slides: posts
                    });
                    if( !this.objEq(stored, current) ) {
                        this.autosaveOpen = true;
                        let dialog = this.dialog.open(AutosaveDialog, {
                            data: {
                                date: data.date
                            }
                        });
                        dialog.afterClosed().subscribe(cancel => {
                            this.autosaveOpen = false;
                            if( !cancel ) {
                                this.assignment = new Assignment(stored.assignment);
                                this.assignment.clones = clones;
                                this.slides = stored.slides.map(item => {
                                    return new Slide(item);
                                });
                                this.getImages();
                                this.getClones();
                            }
                        });
                    }
                }
            }
        );
    }
    start() {
        this.heartbeatSubscription = this.api.heartbeat
            .pipe(
                takeWhile(val => this.assignment !== null)
            ).subscribe(() => {
                this.lockCheck();
                this.maybeAutosave();
            });
    }
    grab(actionsEl) {
        // start heartbeat after autosave has been checked
        let autosaveSubscription = this.autosaveChecked.subscribe(checked => {
            this._autosaveChecked = true;
            this.start();
            autosaveSubscription.unsubscribe();
        });

        // get autosave after assignment is unlocked
        let lockSubscription = this._locked.subscribe(locked => {
            this.lastModified = this.assignment.modified_gmt;
            this.backupLocally();
            this.getAutosave(this.assignment);
            lockSubscription.unsubscribe();
        });

        this.setLock();
    }
    updateMessages() {
        const team = this.assignment.team;
        const blog_ids = this.assignment.sites.map(item => item.blog_id);
        const sites = this.api.getProp('sites');
        this.messages = [];
        for(var i = 0; i < team.length; i++) {
            const member = team[i];
            const member_blogs = member.limited;
            if( member_blogs && member_blogs.length > 0 ) {
                let memberNeedsBlog = true;
                for(var j = 0; j < blog_ids.length; j++) {
                    if( member_blogs.indexOf(blog_ids[j]) !== -1 ) {
                        memberNeedsBlog = false;
                        break;
                    }
                }
                if( memberNeedsBlog ) {
                    const domains = sites.filter(item => member_blogs.indexOf(item.blog_id) !== -1).map(item => item.domain);
                    this.messages.push(member.name + ' needs ' + domains.join(' or ') + ' for access to this assignment');
                }
            }
        }
    }
    userCanView(assignment) {
        const user = this.api.getProp('user');
        if( user.limited.length === 0 || assignment.author == user.id ) {
            return true;
        }
        const blog_ids = assignment.sites.map(item => item.blog_id);
        for( var i = 0; i < blog_ids.length; i++ ) {
            if( user.limited.indexOf(blog_ids[i]) !== -1 ) {
                return true;
            }
        }
        let author = this.userService.getUser(assignment.author);
        const dialogRef = this.dialog.open(CannotViewDialog, {
            data: author
        });
        dialogRef.afterClosed().subscribe(data => {
            this.router.navigate(['/assignments', 'queue']);
        });
        return false;
    }
    setSlideshow(assignment) {
        if( !this.userCanView(assignment) ) {
            return;
        }
        let quiz = assignment.childType == 'quiz' ? true : false;
        this.quiz = quiz;
        this.assignment = assignment;
        this._assignmentLoaded = true;
        this.assignmentLoaded.emit(this.assignment);
        this.getClones();
        this.updateMessages();
        this.title = assignment.title;
        this.slides = [];
        if( assignment.slides ) {
            this.getSlides(this.assignment.slides);
        } else {
            this.createSlide(); // create first slide
            if( this.assignment.outro ) { // create outro slide too
                this.createSlide();
            }
            this.slidesLoaded.emit(this.slideIndex);
        }
        let title = 'Slideshow | ' + assignment.title;
        if( quiz ) {
            title = 'Quiz | ' + assignment.title;
            if( assignment.quizType === 'personality' ) {
                this.answerSummaryPlaceholder = 'Generalized Question Summary';
            }
        }
        this.titleService.setTitle('Swarm | ' + title);
    }
    getChunk(chunk, index) {
        return this.api.doAction('post', 'slides', {
            action: 'read',
            include: chunk,
            page: index + 1
        });
    }
    getChunks(chunks) {
        return from(chunks).pipe(
            concatMap( // performs all requests sequentially
                (chunk, index) => from(this.getChunk(chunk, index)).pipe(
                    catchError(error => of(error)) // allows error handling per request
                )
            )
        );
    }
    getSlides(slides) {
        if( !slides ) return;
        // chunk load slides, concatMap to preserve order
        let slideArr = [];
        slides += ''; // convert to string
        if( slides.indexOf(',') === -1 ) {
            slideArr = [slides];
        } else {
            slideArr = slides.split(',');
        }
        let len = slideArr.length;
        let chunks = [];
        let size = 20;
        for (let i = 0; i < len; i += size) {
            let chunk = slideArr.slice(i, i + size);
            chunks.push(chunk.join(','));
        }
        this.getChunks(chunks).pipe(
            takeWhile(val => this.assignment !== null),
            finalize(() => {
                this.slidesLoaded.emit(this.slideIndex);
                if( this.quiz ) {
                    this.calculateTiers();
                }
            })
        ).subscribe(
            (data) => {
                let slides = data.map(item => {
                    item = new Slide(item);
                    return item;
                });
                
                for( var i = 0; i < slides.length; i++) {
                    this.slides.push(slides[i]);
                }

            }
        );
    }
    setSlide(index) {
        this.slideIndex = index;
        this.scroll(index);
        this.updateLocation(index);
    }
    scroll(index) {
        let anchor = document.getElementById('slide-' + index);
        if( anchor ) {
            anchor.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }
    updateLocation(index) {
        if( !this.assignment ) return;
        let path = this.location.path();
        let prefix = '/slideshow/' + this.assignment.id + '/';
        let pathslide = path.replace(prefix, '');
        if( index != pathslide ) {
            this.location.replaceState(prefix + index);
        }
    }
    setSlideOrder() {
        let slidesIds = [];
        let intro = this.assignment.intro;
        let outro = this.assignment.outro;
        let last = this.slides.length - 1;
        this.slides.forEach((slide, index) => {
            slidesIds.push(slide.id);
            slide.intro = index == 0 ? intro : 0;
            slide.outro = ( index == last && last > 1 ) ? outro : 0;
        });
        this.assignment.slides = slidesIds.join(',');
    }
    newSlide() {
        let slide = new Slide({});
        let slides = this.slides;
        let length = slides.length;
        let intro = this.assignment.intro;
        let outro = this.assignment.outro;
        let last = length - 1;
        slide.intro = length == 0 ? intro : 0; // if this is the first slide set the intro
        slide.outro = length == 1 ? outro : 0; // set outro if we need one
        if( outro && !slide.intro && !slide.outro ) { // insert before outro
            length = last;
        }
        this.slides.splice(length, 0, slide);
        if( this.quiz ) {
            this.calculateTiers()
        }
        this.titleChange(slide);
        this.setSlide(length);
        return {
            slide: slide,
            index: length
        };
    }
    relocate(index, slide) {
        let slides = this.slides;
        let assignment = this.assignment;
        let router = this.router;
        let prev = index - 1;
        let next = index + 1;
        let i = 0;

        if( slides.length == 0 ) {
            this.createSlide();
            return;
        }
        
        if( slides[prev] ) {
            i = prev;
        } else if( slides[next] ) {
            i = next;
        }
        
        router.navigate(['slideshow', assignment.id, i]);
    }
    removeSlide(index) {
        let slide = this.slides[index];
        if( !slide.id ) {
            this.slides.splice(index, 1);
            if( this.quiz ) {
                this.calculateTiers()
            }
        } else {
            let dialogData = {
                title: 'Are you sure you wish to delete this slide?',
                messages: ['Slide ' + index + ': ' + slide.title]
            }
            let dialogRef = this.dialog.open(DeleteDialog, {
                data: dialogData
            });
            dialogRef.afterClosed().subscribe(returnData => {
                if( returnData === true ) {
                    this.api.doAction('post', 'slides', {
                        action: 'delete',
                        id: slide.id
                    }).subscribe(
                        data => {
                            // data = slide in case we want an undo option
                            this.slides.splice(index, 1);
                            let slideIds = this.slides.map(slide => slide.id);
                            this.assignment.slides = slideIds.join(',');
                            if( this.quiz ) {
                                this.calculateTiers()
                            }
                        }
                    );
                }
            });
        }
        if( index == this.slideIndex ) {
            this.relocate(index, slide);
        }
    }
    updateSlideIds() {
        let slideIds = this.slides.map(slide => slide.id).join(',');
        this.api.doAction('post', 'meta', {
            id: this.assignment.id,
            action: 'update',
            fields: [
                {
                    name: 'slides',
                    value: slideIds
                }
            ]
        })
        .pipe(
            takeWhile(val => this.slides.length !== 0),
            finalize(() => {
                this.saving = false;
            })
        )
        .subscribe();
    }
    maybeAddAwswers(slide) {
        if( this.assignment.quizType !== 'personality' ) {
            return;
        }
        const slides = this.slides;
        const lastIndex = this.slides.length - 1;
        const results = slides[lastIndex].content;
        slide.content[0].bucket = 0;
        for(var i = 1; i < results.length; i++) {
            slide.content.push({text: '', correct: false, bucket: i});
        }
    }
    createSlide() {
        this.saving = true;
        let newSlide = this.newSlide();
        let index = newSlide.index;
        let slide = newSlide.slide;
        this.maybeAddAwswers(this.slides[index]);
        this.api.doAction('post', 'slides', {
            action: 'update',
            slide: slide
        })
            .pipe(
                takeWhile(val => this.assignment !== null),
                finalize(() => {
                    this.saving = false;
                })
            ).subscribe(
                data => {
                    this.slides[index].id = data.id;
                    this.updateSlideIds();               
                }
            )
    }
    saveSlide(slide) {
        if( slide.headers[0] == '' ) {
            slide.headers[0] = slide.title;
        }
        return this.api.doAction('post', 'slides', {
            action: 'update',
            slide: slide
        });
    }
    allSitesHaveCategory() {
        const sites = this.assignment.sites;
        for(var i = 0; i < sites.length; i++) {
            if( sites[i].categories.length === 0 ) {
                return false;
            }
        }
        return true;
    }
    getQuizAnswerMessage() {
        let messages = [];
        if( !this.quiz ) {
            return messages;
        }
        const slides = this.slides;
        const type = !this.assignment.quizType ? 'normal' : this.assignment.quizType;
        const message = type == 'personality' ? ' needs personality answers.' : ' needs a correct answer.';
        for(var i = 0; i < slides.length; i++) {
            const slide = slides[i];
            if( !slide.intro && !slide.outro ) {
                const content = slide.content;
                let hasAnswer = false;
                for(var j = 0; j < content.length; j++) {
                    const answer = content[j];
                    hasAnswer = type === 'personality' ? true : false;
                    if( type == 'normal' && answer.correct === true ) {
                        hasAnswer = true;
                        break;
                    } else if( type == 'personality' && (isNaN(answer.bucket) || answer.text == '') ) {
                        hasAnswer = false;
                        break;
                    }
                }
                if( hasAnswer === false ) {
                    messages.push('Slide ' + i + message);
                }
            }
        }
        return messages;
    }
    maybePublish() {
        let error = false;
        let data = {
            title: 'Publish Error',
            messages: [],
            allowPublish: false
        };
        if( this.assignment.articleTitle == '' ) {
            error = true;
            data.messages.push(['Please add an article title.']);
            this.warning = true;
        }
        if( this.assignment.sites.length == 0 ) {
            error = true;
            data.messages.push(['Please select a site.']);
        }
        if( !this.allSitesHaveCategory() ) {
            error = true;
            data.messages.push(['Please add a category for each site.']);
        }
        const quizMessages = this.getQuizAnswerMessage();
        if( quizMessages.length > 0 ) {
            error = true;
            data.messages = data.messages.concat(quizMessages);
        }

        if( !error ) {
            data = {
                title: 'Are you sure?',
                messages: [],
                allowPublish: true
            }
        }
        let dialogRef = this.dialog.open(PublishDialog, {
            data: data
        });
        dialogRef.afterClosed().subscribe(data => {
            if( data === true ) {
                this.saveSlides(true, true);
            }
        });
    }
    maybeNotify() {
        const prev = this.assignment.previousStatus;
        const next = this.assignment.status;
        if( prev == 'unassigned' && next == 'assigned' ) {
            this.notifyService.statusUpdate('unassigned', 'assigned', this.assignment);
            this.assignment.previousStatus = '';
        }
    }
    saveSlides(publish, clearLock) {
        this.warning = false;
        if( this.saving || this.publishing ) return;
        this.stop();

        this.publishing = publish;
        this.saving = true;

        const backupSlides = this.backup ? this.backup.slides : [];
        const currentSlides = this.slides;

        let slidesToSave = [];
        if( !this.objEq(backupSlides, currentSlides) ) {
            for(var i = 0; i < currentSlides.length; i++) {
                const currentSlide = currentSlides[i];
                const backupSlide = backupSlides[i];
                if( !this.objEq(currentSlide, backupSlide) ) {
                    slidesToSave.push(currentSlide);
                }
            }
        }

        this.slidesCompleteSubscription = this.slidesComplete.subscribe(() => {
            this.saveAssignment();
            this.setSEOData();
            this.slidesCompleteSubscription.unsubscribe();
        });
        this.assignmentCompleteSubscription = this.assignmentComplete.subscribe(() => {
            this.saving = false;
            this.backup = this.getBackup(this);
            this.maybeNotify();
            if( publish ) {
                this.publish();
            }
            if( !publish && clearLock ) {
                this.clearLock();
            }
            this.deleteAutosave();
            if( !publish && !clearLock ) {
                this.start();
            }
            this.assignmentCompleteSubscription.unsubscribe();
        });
        from(slidesToSave).pipe(
            mergeMap( // performs all requests in parallel
                slide => from(this.saveSlide(slide)).pipe(
                    catchError(error => of(error)) // allows error handling per request
                )
            ),
            takeWhile(val => this.assignment !== null),
            finalize(() => {
                this.slidesComplete.emit();
                console.log('Saving slides complete.');
            })
        ).subscribe();
    }
    saveAssignment() {
        if( !this.assignment ) {
            return;
        }
        let slideIds = this.slides.filter(item => item.hasOwnProperty('id')).map(slide => {
            return slide.id;
        });
        this.assignment.slides = slideIds.join(',');
        this.api.doAction('post', 'assignments', {
            action: 'update',
            assignment: this.assignment
        })
            .pipe(
                takeWhile(val => this.assignment !== null),
                finalize(() => {
                    this.assignmentComplete.emit();
                })
            ).subscribe();
    }
    clearLock() {
        this.stop();
        this.locked = true;
        this._locked.emit(this.locked);
        this.assignmentService.deleteLock(this.assignment);
    }
    padZero(num) {
        return ('0' + num).slice(-2);
    }
    getDate(date) {
        return date.getFullYear() + '-' + [
            (date.getMonth()+1),
            date.getDate()
        ]
        .map(num => this.padZero(num))
        .join('-') + ' ' + [
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
        ]
        .map(num => this.padZero(num)).join(':');
    }
    maybeSetStatus(status) {
        let dialogRef = this.dialog.open(SetStatusDialog, {
            data: { 
                title: 'Ready to Publish',
                status: status,
                targetPublishDate: this.assignment.targetPublishDate
            }
        });
        dialogRef.afterClosed().subscribe(info => {
            if( info ) {
                this.assignment.targetPublishDate = info.targetPublishDate ? this.getDate(info.targetPublishDate) : '';
                this.targetPublishDateChange.emit();
                this.saving = true;
                this.assignmentCompleteSubscription = this.assignmentComplete.subscribe(() => {
                    this.saving = false;
                    this.assignmentCompleteSubscription.unsubscribe();
                });
                this.setStatus(status);
                this.saveAssignment();
            }
        });
    }
    maybeUpdateStatus() {
        if( this.assignment.status == 'imported' ) {
            return;
        }
        const user = this.api.getProp('user');
        if( user.id == this.assignment.pageAddWriter && this.assignment.status == 'add_assigned' ) {
            this.setStatus('add_sent_to_editor');
            return;
        }

        if( !this.assignment.published || this.assignment.status == 'revision_requested' ) {
            this.setStatus('sent_to_editor');
        }
    }
    submit() {
        let total = this.slides.length;
        let slideCount = this.assignment.slideCount;
        if( total < slideCount ) {
            let dialogRef = this.dialog.open(SendToEditorDialog, {
                data: { total: total, slideCount: slideCount },
            });
            dialogRef.afterClosed().subscribe(data => {
                if( data !== true ) {
                    this.maybeUpdateStatus();
                    this.saveSlides(false, true);
                    this.addComment(data);
                }
            });
        } else {
            this.maybeUpdateStatus();
            this.saveSlides(false, true);
        }
    }
    addComment(text) {
        if( !text ) {
            return;
        }
        let user = this.api.getProp('user');
        let id = this.assignment.id;
        let date = new Date().toISOString();
        let comment = {
            author: user.id,
            abbreviation: user.abbreviation,
            role: user.role,
            content: text,
            text: text,
            date: date,
            post: id
        };
        this.api.doAction('post', 'comment', {
            action: 'create',
            comment: comment
        })
            .pipe(
                takeWhile(val => this.assignment !== null)
            ).subscribe(
                data => {
                    this.commentAdded.emit(comment);
                    this.notifyService.comment(comment, this.assignment);
                }
            );
    }
    purgeCache() {
        this.api.doAction('post', 'purge', this.assignment).subscribe(
            data => {
                console.log(data);
            }
        );
    }
    updateSlideStatus(status, slideIds) {
        const slides = this.slides;
        for(var i = 0; i < slides.length; i++) {
            const slideId = slides[i].id;
            if( slideId && slideIds.indexOf(slideId) ) {
                slides[i].status = status;
            }
        }
    }
    publish() {
        let copy = {...this.assignment};
        let prevStatus = copy.status;
        this.notifyService.statusUpdate(prevStatus, 'publish', this.assignment);
        this.assignment.status = 'publish';
        this.api.doAction('post', 'publish', this.assignment)
            .pipe(
                takeWhile(val => this.assignment !== null),
                finalize(() => {
                    this.publishing = false;
                    this.clearLock();
                })
            ).subscribe(
                data => {
                    this.publishing = false;
                    this.clearLock();
                    this.assignment.sites = data.sites;
                    this.assignment.published = data.published;
                    this.updateSlideStatus('publish', data.slides);
                    this.purgeCache();
                }
            );
    }
    sendPageAdditions() {
        this.sending = true;
        this.assignmentCompleteSubscription = this.assignmentComplete.subscribe(() => {
            this.sending = false;
            this.assignmentCompleteSubscription.unsubscribe();
        });
        this.setStatus('add_assigned');
        this.saveAssignment();
    }
    sendRevisionRequest() {
        this.sending = true;
        this.assignmentCompleteSubscription = this.assignmentComplete.subscribe(() => {
            this.sending = false;
            this.assignmentCompleteSubscription.unsubscribe();
        });
        this.setStatus('revision_requested');
        this.saveAssignment();
    }
    setStatus(status) {
        let copy = { ...this.assignment };
        let prevStatus = copy.status;
        this.assignment.status = status;
        this.notifyService.statusUpdate(prevStatus, status, this.assignment);
        this.statusChange.emit();
    }
    getSlideID(slide, index) {
        if( !slide || !slide.hasOwnProperty('id') ) {
            return '';
        }
        return 'ID: ' + slide.id;
    }
    copy() {
        if( this.locked ) {
            return;
        }
        const copy = JSON.parse(JSON.stringify(this.assignment));
        copy.title += ' - Copy ' + (this.clones.length + 1);
        let cloneSlugs = [];
        const clones = this.clones;
        for(var i = 0; i < clones.length; i++) {
            const sites = clones[i].sites;
            for(var j = 0; j < sites.length; j++) {
                const slug = sites[j].slug;
                if( slug ) {
                    cloneSlugs.push(slug);  
                }
                const desiredSlug = sites[j].desiredSlug;
                if( desiredSlug ) {
                    cloneSlugs.push(desiredSlug);  
                }
            }
        }
        const dialog = this.dialog.open(CopyDialog, {
            panelClass: 'allow-overflow',
            data: {
                copy: copy,
                slugs: cloneSlugs
            }
        });
        dialog.afterClosed().subscribe(data => {
            if( data ) {
                this.assignment.clones = data.clones;
                this.clones.push(data.clone);
            }
        });
    }
}
@Component({
    selector: 'copy-dialog',
    template: `
        <h2 mat-dialog-title>Copy Assignment</h2>
        <mat-dialog-content id="copy">
            <label>Title</label>
            <textarea 
                [(ngModel)]="data.copy.title" 
                (input)="clearError()" 
                placeholder="Title">
            </textarea>
            <label>Abbreviation</label>
            <input class="abbr" [(ngModel)]="data.copy.abbreviation" (input)="clearError()" placeholder="Abbreviation"/>
            <label>Slug</label>
            <slug-component 
                *ngIf="primary" 
                [(slug)]="primary.slug"
                [allowEmpty]="true"
                [blog_id]="primary.blog_id"
                (propChange)="clearError($event)">
            </slug-component>
            <properties-component 
                [(properties)]="data.copy.sites"
                [canEdit]="true">
            </properties-component>
        </mat-dialog-content>
        <mat-dialog-actions>
            <span class="error" [innerHTML]="error"></span>
            <img class="loader" *ngIf="saving" src="/wp-content/themes/swarm/app/assets/images/loading.gif">
            <button class="button" [mat-dialog-close]="false">Cancel</button>
            <button class="button" [disabled]="available === false" (click)="copy()">Copy</button>
        </mat-dialog-actions>`,
})
export class CopyDialog {
    saving = false;
    error = '';
    primary;
    available = false;
    checked = false;
    constructor(@Inject(MAT_DIALOG_DATA) public data: any, private api: ApiService, private ref: MatDialogRef<CopyDialog>) {
        const primary = data.copy.sites.filter(item => item.primary).shift();
        if( primary ) {
            this.primary = JSON.parse(JSON.stringify(primary));
        }
        data.copy.sites = data.copy.sites.map(item => {
            item = new Site({
                abbreviation: item.abbreviation,
                blog_id: item.blog_id,
                domain: item.domain,
                categories: JSON.parse(JSON.stringify(item.categories))
            });
            const metaKeys = Object.keys(item.meta);
            for(var i = 0; i < metaKeys.length; i++) {
                const key = metaKeys[i];
                if( key.indexOf('hide') !== -1 ) {
                    item.meta[key] = true;
                }
            }
            return item;
        });
    }
    clearError(e) {
        if( e ) {
            this.available = e.available;
            this.checked = e.checked;
        }
        this.error = '';
        if( this.available === false && this.checked === true ) {
            this.error = 'Please choose a different slug.';
        }
    }
    hasSlug() {
        return this.data.slugs.indexOf(this.primary.slug) !== -1;
    }
    copy() {
        if( this.available === false ) {
            this.error = 'Please choose a different slug.';
            return;
        }
        if( this.hasSlug() ) {
            this.error = 'Please choose a different slug.';
            return;
        }
        this.saving = true;
        this.data.copy.sites = this.data.copy.sites.map(item => {
            item.desiredSlug = this.primary.slug;
            return item;
        });
        this.api.doAction('post', 'clone', {
            action: 'copy',
            data: this.data.copy
        }).pipe(
            finalize(() => {
                this.saving = false;
            })
        ).subscribe(
            data => {
                this.ref.close(data);
            },
            err => {
                console.log(err);
                this.error = err.hasOwnProperty('error') ? err.error.message : err.message;
                throw err;
            }
        );
    }
}
@Component({
    selector: 'takeover-dialog',
    template: `
        <h2 mat-dialog-title>Assignment Locked</h2>
        <mat-dialog-content>{{data.user}} is currently editing this assignment. Would you like to take over?</mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button [mat-dialog-close]="true">Cancel</button>
            <button mat-button mat-dialog-close>Yes</button>
        </mat-dialog-actions>`,
})
export class TakeoverDialog {
    constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}
}
@Component({
    selector: 'autosave-dialog',
    template: `
        <h2 mat-dialog-title>Restore Assignment</h2>
        <mat-dialog-content>There is an autosave of this assignment from {{data.date | date: 'medium'}}. Would you like to restore?</mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button [mat-dialog-close]="true">Cancel</button>
            <button mat-button mat-dialog-close>Yes</button>
        </mat-dialog-actions>`,
})
export class AutosaveDialog {
    constructor(@Inject(MAT_DIALOG_DATA) public data: any) {}
}
@Component({
    selector: 'send-to-editor-dialog',
    template: `
        <h2 mat-dialog-title>Send to Editor</h2>
        <mat-dialog-content fxLayout="column" fxLayoutGap="10px">
            <div *ngIf="!confirmed">Are you sure you want to send {{data.total}} of {{data.slideCount}} slides?</div>
            <mat-form-field *ngIf="confirmed" id="send-to-editor">
                <textarea matInput [(ngModel)]="notes" placeholder="Add a note" (keyup.enter)="send()"></textarea>
            </mat-form-field>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button [mat-dialog-close]="true">Cancel</button>
            <button mat-button *ngIf="!confirmed" (click)="confirm()">Yes</button>
            <button mat-button *ngIf="confirmed" [mat-dialog-close]="notes">Send</button>
        </mat-dialog-actions>`,
})
export class SendToEditorDialog {
    confirmed = false;
    notes = '';
    confirm() {
        this.confirmed = true;
    }
    constructor(@Inject(MAT_DIALOG_DATA) public data: any, private ref: MatDialogRef<SendToEditorDialog>) { }
    send() {
        this.ref.close(this.notes);
    }
}
@Component({
    selector: 'set-status-dialog',
    template: `
        <h2 mat-dialog-title>{{data.title}}</h2>
        <mat-dialog-content fxLayout="column" fxLayoutGap="10px">
            <mat-form-field *ngIf="showDate">
                <input matInput [matDatepicker]="picker" placeholder="Target Publish Date" [(ngModel)]="info.targetPublishDate" [min]="minDate">
                <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
            </mat-form-field>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button [mat-dialog-close]="false">Cancel</button>
            <button mat-button [mat-dialog-close]="info">Update</button>
        </mat-dialog-actions>`,
})
export class SetStatusDialog {
    info = {
        targetPublishDate: new Date()
    };
    minDate = new Date();
    showDate = false;
    constructor(@Inject(MAT_DIALOG_DATA) public data: any) {
        if( data.status == 'publish_ready' ) {
            this.showDate = true;
            this.info.targetPublishDate = data.targetPublishDate ? new Date(data.targetPublishDate) : this.info.targetPublishDate;
        }
    }
}
@Component({
    selector: 'delete-dialog',
    template: `
        <h2 mat-dialog-title>{{data.title}}</h2>
        <mat-dialog-content>
            <p *ngFor="let message of data.messages; trackBy: index">{{message}}</p>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button [mat-dialog-close]="false">Cancel</button>
            <button mat-button [mat-dialog-close]="true">Delete</button>
        </mat-dialog-actions>`,
})
export class DeleteDialog {
    constructor( @Inject(MAT_DIALOG_DATA) public data: any) { }
}
@Component({
    selector: 'publish-dialog',
    template: `
        <h2 mat-dialog-title>{{data.title}}</h2>
        <mat-dialog-content>
            <p *ngFor="let message of data.messages; trackBy: index">{{message}}</p>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button mat-button [mat-dialog-close]="false">Cancel</button>
            <button mat-button *ngIf="data.allowPublish === true" [mat-dialog-close]="true">Publish</button>
        </mat-dialog-actions>`,
})
export class PublishDialog {
    constructor( @Inject(MAT_DIALOG_DATA) public data: any) { }
}
@Component({
    selector: 'cannot-view-dialog',
    template: `
        <h2 mat-dialog-title>Access Denied</h2>
        <mat-dialog-content>
            <p>Sorry! You are not allowed to view this assignment. <ng-container *ngIf="data">Please contact {{data.name}} for access.</ng-container></p>
        </mat-dialog-content>
        <mat-dialog-actions>
            <button class="button" mat-dialog-close>My Queue</button>
        </mat-dialog-actions>`,
})
export class CannotViewDialog {
    constructor(@Inject(MAT_DIALOG_DATA) public data: any) { }
}


// WEBPACK FOOTER //
// /opt/atlassian/pipelines/agent/build/src/swarm/src/app/slideshow/slideshow.service.ts